// subscriptionGate is the SPA-side mirror of the backend's
// middleware.RequireActiveSubscription write-gate. The backend already
// 402s mutating requests once a tenant's subscription_status goes
// inactive (trial_expired / past_due / canceled / paused); this helper
// is the PROACTIVE half — the router uses it to bounce a frozen
// operator to the /abonnement interstitial before the dashboard paints,
// instead of letting the whole dashboard render read-only and only
// tripping the 402 on the first write.
//
// The cardless trial is deliberately NOT gated: a `trialing`
// (and `active` / `comp` / `incomplete`) status passes, so a brand-new
// operator keeps full access with no plan and no card. Only the
// post-trial / payment-failure freeze bounces to the paywall.
//
// Pure functions so the router guard stays a one-liner and the truth
// table is unit-tested in subscriptionGate.spec.ts.

// INACTIVE_SUBSCRIPTION_STATUSES must stay in lockstep with the backend
// `inactiveStatuses` set in internal/middleware/subscription_gate.go.
// Listed explicitly (not "everything except trialing/active") so a
// future status we haven't mapped can't accidentally freeze an operator
// out of their dashboard — belt-and-braces, same posture as the backend.
// `incomplete` is intentionally ABSENT (mirrors the backend): the
// operator just clicked subscribe and Stripe is mid-3DS; locking them
// out during the SCA handshake is worse UX than letting them in for the
// brief window.
export const INACTIVE_SUBSCRIPTION_STATUSES = new Set<string>([
  'trial_expired',
  'past_due',
  'canceled',
  'paused',
])

// The standalone full-screen interstitial the guard redirects to. Lives
// OUTSIDE the /dashboard subtree so it renders without the dashboard
// chrome (matches the awaiting-review interstitial's placement).
export const SUBSCRIPTION_INTERSTITIAL_PATH = '/abonnement'

// isSubscriptionInactive reports whether a status freezes the operator
// dashboard. Empty / undefined / unknown statuses return false — a
// missing /me field (legacy backend, race against fetch) must degrade
// OPEN so a stale response never traps a paying operator behind the
// paywall. The reverse default would lock working sessions out on any
// backend hiccup.
export function isSubscriptionInactive(status: string | null | undefined): boolean {
  if (!status) return false
  return INACTIVE_SUBSCRIPTION_STATUSES.has(status)
}

// pathIsSubscriptionRecoverySurface returns true for the routes that
// must stay reachable while frozen so the operator can recover billing
// or manage personal email preferences. Billing lives under /dashboard/settings (tab=billing) — the same
// surface the backend write-gate exempts (/api/admin/billing*). Gating
// it would strand a frozen org with no way to recover. The interstitial
// itself is outside /dashboard, so the dashboard-scoped guard never
// touches it.
//
// The boundary check (exact match OR a trailing slash) prevents a
// future /dashboard/settings-export from being caught accidentally —
// same fence shape shouldRequirePasskey uses.
function pathIsSubscriptionRecoverySurface(path: string): boolean {
  // Personal email opt-out must remain reachable after the trial ends.
  // The backend still gates unrelated account mutations.
  if (path === '/dashboard/account') return true
  if (path === '/dashboard/settings') return true
  if (path.startsWith('/dashboard/settings/')) return true
  return false
}

// pathIsGatedDashboard pins the gate to the operator dashboard subtree,
// matching the backend RequireActiveSubscription placement. The booking
// flow, customer portal, and marketing site have their own gates (the
// public booking surface is closed separately via
// BlockNewBookingsWhenInactive) and must never be caught here.
function pathIsGatedDashboard(path: string): boolean {
  if (path === '/dashboard') return true
  if (path.startsWith('/dashboard/')) return true
  return false
}

// shouldBlockForSubscription returns the interstitial path to redirect
// to, or null when the navigation is allowed through. A hit means:
// the target is inside the gated dashboard subtree, the subscription is
// frozen, and the target is neither billing nor personal settings.
//
// Accepts either a bare path (Vue Router `to.path`) or a full path with
// query/hash (`to.fullPath`) — the query and hash are stripped first so
// `/dashboard/settings?tab=billing` still resolves to the recovery
// surface. The sibling shouldRequirePasskey guard is called with
// `to.fullPath`, so being tolerant here avoids a footgun if a future
// caller does the same.
export function shouldBlockForSubscription(
  path: string,
  status: string | null | undefined,
): string | null {
  const bare = path.split(/[?#]/, 1)[0]
  if (!pathIsGatedDashboard(bare)) return null
  if (!isSubscriptionInactive(status)) return null
  if (pathIsSubscriptionRecoverySurface(bare)) return null
  return SUBSCRIPTION_INTERSTITIAL_PATH
}
